<script setup>
import axios from 'axios'
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import Swal from "sweetalert2";

const route   = useRoute();
const router  = useRouter();
const examId  = route.params.id;
const attempt = route.query.attempt;


const loading  = ref(true);
const examData = ref(null);
const unavailable = ref(null);

const fetchExamAnswers = async () => {
  try {
    loading.value = true;
    unavailable.value = null;
    const response = await axios.get('/exam-answers/user', { params: { exam_id: examId, attempt } });
    if (!response.data.status) throw new Error("لا توجد نتيجة لهذا الامتحان");
    examData.value = response.data.data;
  } catch (error) {
    if (error.response?.data?.code === 'answers_unavailable') {
      unavailable.value = error.response.data;
      return;
    }
    Swal.fire({ icon:'error', title:'فشل تحميل النتيجة', text:'الامتحان غير موجود أو لم يتم تسليمه بعد' })
        .then(() => router.push('/my_exams'));
  } finally {
    loading.value = false;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "غير محدد";
  return new Date(dateString).toLocaleString('ar-EG', {
    year:'numeric', month:'long', day:'numeric', hour:'2-digit', minute:'2-digit'
  });
};

const isPassed = (pct) => pct >= 60;

onMounted(() => fetchExamAnswers());
</script>

<template>
  <!-- Loading -->
  <div v-if="loading" class="review-loading">
    <div class="rev-spinner"></div>
    <p>جاري تحميل مراجعة الإجابات...</p>
  </div>

  <div v-else-if="unavailable" class="review-page">
    <div class="review-container" role="status">
      <h1>تم حفظ تسليم الامتحان</h1>
      <p>{{ unavailable.message }}</p>
      <p v-if="unavailable.answers_release_at">موعد العرض: {{ formatDate(unavailable.answers_release_at) }}</p>
      <button type="button" @click="fetchExamAnswers">تحديث</button>
      <router-link to="/my_exams">العودة إلى نتائجي</router-link>
    </div>
  </div>
  <!-- Review page -->
  <div v-else-if="examData" class="review-page">
    <div class="review-container">

      <!-- ── Summary card ── -->
      <div class="summary-card">
        <div class="summary-card-glow" :class="isPassed(examData.percentage) ? 'glow--pass' : 'glow--fail'"></div>

        <h1 class="summary-title">مراجعة الإجابات</h1>

        <!-- Big score circle -->
        <div class="big-score-wrap">
          <div class="big-score-ring" :class="isPassed(examData.percentage) ? 'ring--pass' : 'ring--fail'">
            <div class="big-score-inner">
              <span class="big-pct">{{ examData.percentage }}%</span>
              <span class="big-label">{{ isPassed(examData.percentage) ? 'ناجح 🎉' : 'راسب 😔' }}</span>
            </div>
          </div>
        </div>

        <!-- Stats row -->
        <div class="summary-stats">
          <div class="sum-stat">
            <span class="sum-stat-num">{{ examData.student_score }}</span>
            <span class="sum-stat-den"> / {{ examData.exam_total_score }}</span>
            <span class="sum-stat-label">درجتك</span>
          </div>
          <div class="sum-divider"></div>
          <div class="sum-stat">
            <span class="sum-stat-num" style="color:#22C55E">
              {{ examData.answers?.filter(a => a.student_is_correct).length || 0 }}
            </span>
            <span class="sum-stat-label">إجابة صحيحة</span>
          </div>
          <div class="sum-divider"></div>
          <div class="sum-stat">
            <span class="sum-stat-num" style="color:#EF4444">
              {{ examData.answers?.filter(a => !a.student_is_correct).length || 0 }}
            </span>
            <span class="sum-stat-label">إجابة خاطئة</span>
          </div>
        </div>

        <!-- Motivational message -->
        <div class="motivational" :class="isPassed(examData.percentage) ? 'motiv--pass' : 'motiv--fail'">
          {{ isPassed(examData.percentage)
              ? '🌟 أحسنت! أداء رائع — استمر في التميز!'
              : '💪 لا تستسلم! راجع الإجابات وحاول مرة أخرى!' }}
        </div>
      </div>

      <!-- ── Questions review ── -->
      <div class="questions-review">
        <h2 class="section-heading">📋 مراجعة الأسئلة</h2>

        <div class="question-review-card"
          v-for="(answer, index) in examData.answers"
          :key="answer.question_id"
          :class="answer.student_is_correct ? 'qrc--correct' : 'qrc--wrong'"
        >
          <!-- Question header -->
          <div class="qrc-header">
            <div class="qrc-num" :class="answer.student_is_correct ? 'qnum--correct' : 'qnum--wrong'">
              {{ index + 1 }}
            </div>
            <h3 class="qrc-title">{{ answer.question_title }}</h3>
            <div class="qrc-degree">{{ answer.degree }} درجة</div>
          </div>

          <!-- Answer blocks -->
          <div class="qrc-answers">
            <!-- Student answer -->
            <div class="answer-block" :class="answer.student_is_correct ? 'ab--correct' : 'ab--wrong'">
              <div class="ab-icon">
                <svg v-if="answer.student_is_correct" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                <svg v-else viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </div>
              <div class="ab-body">
                <span class="ab-label">إجابتك:</span>
                <span class="ab-text">{{ answer.answer_title || answer.text_answer || 'لم تجب على هذا السؤال' }}</span>
              </div>
              <div class="ab-score">{{ answer.score }} / {{ answer.degree }}</div>
            </div>

            <!-- Correct answer (if wrong) -->
            <div v-if="!answer.student_is_correct" class="answer-block ab--correct-reveal">
              <div class="ab-icon ab-icon--success">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
              </div>
              <div class="ab-body">
                <span class="ab-label">الإجابة الصحيحة:</span>
                <span class="ab-text">{{ answer.correct_answer?.title }}</span>
              </div>
            </div>
          </div>

          <div class="qrc-timestamp">تمت الإجابة في: {{ formatDate(answer.answered_at) }}</div>
        </div>
      </div>

      <!-- ── Actions ── -->
      <div class="review-actions">
        <button @click="router.push('/my_exams')" class="btn-back">
          ← العودة إلى نتائجي
      </button>
        <button @click="router.push('/exams?type=online')" class="btn-retry">
          🔄 امتحان جديد
        </button>
      </div>

    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.review-page {
  background: #0D0D0D; direction: rtl;
  min-height: calc(100vh - 70px);
  font-family: 'Cairo', sans-serif; padding: 2rem 0 4rem;
}
.review-loading {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; min-height: calc(100vh - 70px);
  gap: 1.25rem; font-family: 'Cairo', sans-serif; color: rgba(240,237,228,0.6); font-size: 1.1rem;
}
.rev-spinner {
  width: 64px; height: 64px; border: 5px solid rgba(212,175,55,.15);
  border-top-color: #D4AF37; border-radius: 50%; animation: spin 1s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.review-container { max-width: 900px; margin: 0 auto; padding: 0 1.25rem; display: flex; flex-direction: column; gap: 2rem; }

/* ─── Summary card ─── */
.summary-card {
  background: #fff; border-radius: 24px; padding: 2.5rem 2rem;
  box-shadow: 0 20px 60px rgba(0,0,0,.1); text-align: center;
  position: relative; overflow: hidden;
}
.summary-card-glow {
  position: absolute; top: -60px; left: 50%; transform: translateX(-50%);
  width: 300px; height: 300px; border-radius: 50%; pointer-events: none;
  opacity: .3;
}
.glow--pass { background: radial-gradient(circle,#22C55E,transparent); }
.glow--fail  { background: radial-gradient(circle,#EF4444,transparent); }

.summary-title { font-size: 1.6rem; font-weight: 800; color: #1A1A1A; margin-bottom: 1.75rem; position: relative; z-index: 1; }

.big-score-wrap { display: flex; justify-content: center; margin-bottom: 2rem; position: relative; z-index: 1; }
.big-score-ring {
  width: 150px; height: 150px; border-radius: 50%;
  padding: 5px; display: flex; align-items: center; justify-content: center;
}
.ring--pass { background: linear-gradient(135deg,#22C55E,#16a34a); box-shadow: 0 20px 50px rgba(34,197,94,.35); }
.ring--fail  { background: linear-gradient(135deg,#EF4444,#dc2626); box-shadow: 0 20px 50px rgba(239,68,68,.35); }
.big-score-inner {
  background: #fff; border-radius: 50%;
  width: 100%; height: 100%;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: .25rem;
}
.big-pct { font-size: 2rem; font-weight: 800; color: #1A1A1A; }
.big-label { font-size: .85rem; font-weight: 700; color: #6B7280; }

.summary-stats { display: flex; justify-content: center; align-items: center; gap: 0; flex-wrap: wrap; margin-bottom: 1.5rem; position: relative; z-index: 1; }
.sum-stat { display: flex; flex-direction: column; align-items: center; padding: .75rem 2rem; gap: .15rem; }
.sum-stat-num { font-size: 1.8rem; font-weight: 800; color: #D4AF37; }
.sum-stat-den { font-size: 1.2rem; color: #9CA3AF; }
.sum-stat-label { font-size: .78rem; color: #9CA3AF; font-weight: 500; }
.sum-divider { width: 1px; height: 60px; background: rgba(212,175,55,.1); align-self: center; }

.motivational {
  padding: .9rem 1.5rem; border-radius: 14px; font-weight: 700; font-size: .95rem;
  position: relative; z-index: 1;
}
.motiv--pass { background: rgba(34,197,94,.08); color: #16a34a; border: 1.5px solid rgba(34,197,94,.2); }
.motiv--fail  { background: rgba(239,68,68,.06); color: #dc2626; border: 1.5px solid rgba(239,68,68,.15); }

/* ─── Section heading ─── */
.section-heading { font-size: 1.25rem; font-weight: 800; color: #F0EDE4; margin-bottom: 1.25rem; }

/* ─── Question review card ─── */
.questions-review { display: flex; flex-direction: column; gap: 1.25rem; }
.question-review-card {
  background: #fff; border-radius: 20px; padding: 1.5rem;
  box-shadow: 0 6px 20px rgba(0,0,0,.07);
  border: 2.5px solid transparent; transition: box-shadow .25s;
}
.question-review-card:hover { box-shadow: 0 12px 35px rgba(0,0,0,.1); }
.qrc--correct { border-color: rgba(34,197,94,.25); }
.qrc--wrong   { border-color: rgba(239,68,68,.2); }

.qrc-header { display: flex; align-items: flex-start; gap: 1rem; margin-bottom: 1.25rem; flex-wrap: wrap; }
.qrc-num {
  width: 48px; height: 48px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.1rem; font-weight: 800; color: #fff;
}
.qnum--correct { background: linear-gradient(135deg,#22C55E,#16a34a); }
.qnum--wrong   { background: linear-gradient(135deg,#EF4444,#dc2626); }

.qrc-title { flex: 1; font-size: 1.1rem; font-weight: 700; color: #1A1A1A; line-height: 1.5; }
.qrc-degree { background: rgba(212,175,55,.08); color: #D4AF37; padding: .3rem .85rem; border-radius: 99px; font-weight: 700; font-size: .82rem; white-space: nowrap; }

/* Answer blocks */
.qrc-answers { display: flex; flex-direction: column; gap: .75rem; margin-bottom: 1rem; }
.answer-block {
  display: flex; align-items: flex-start; gap: .9rem;
  padding: .9rem 1rem; border-radius: 14px;
}
.ab--correct { background: rgba(34,197,94,.06); border: 1.5px solid rgba(34,197,94,.2); }
.ab--wrong   { background: rgba(239,68,68,.05);  border: 1.5px solid rgba(239,68,68,.15); }
.ab--correct-reveal { background: rgba(34,197,94,.06); border: 1.5px solid rgba(34,197,94,.2); }

.ab-icon { width: 32px; height: 32px; flex-shrink: 0; color: #dc2626; }
.ab--correct .ab-icon, .ab--correct-reveal .ab-icon { color: #16a34a; }
.ab-icon--success { color: #16a34a; }
.ab-icon svg { width: 100%; height: 100%; }

.ab-body { flex: 1; }
.ab-label { font-weight: 700; color: #1A1A1A; font-size: .85rem; display: block; margin-bottom: .2rem; }
.ab-text  { color: #334155; font-size: .95rem; }

.ab-score { font-weight: 800; color: #1A1A1A; font-size: 1rem; white-space: nowrap; }

.qrc-timestamp { color: #9CA3AF; font-size: .78rem; text-align: start; }

/* ─── Actions ─── */
.review-actions { display: flex; justify-content: center; gap: 1rem; flex-wrap: wrap; padding-top: 1rem; }
.btn-back, .btn-retry {
  padding: .9rem 2.5rem; border: none; border-radius: 99px;
  font-size: 1rem; font-weight: 700; cursor: pointer;
  transition: all .3s; font-family: 'Cairo', sans-serif;
}
.btn-back {
  background: linear-gradient(135deg,#D4AF37,#C89B2D); color: #fff;
  box-shadow: 0 12px 30px rgba(212,175,55,.3);
}
.btn-back:hover { transform: translateY(-3px); box-shadow: 0 20px 45px rgba(212,175,55,.45); }
.btn-retry {
  background: #F1F5F9; color: #475569;
}
.btn-retry:hover { background: #E2E8F0; transform: translateY(-2px); }

@media(max-width:600px) {
  .summary-card { padding: 2rem 1.25rem; }
  .summary-stats { gap: 0; }
  .sum-stat { padding: .6rem 1rem; }
  .review-actions { flex-direction: column; }
  .btn-back, .btn-retry { width: 100%; text-align: center; }
}
</style>
